import { Actions, Subjects } from 'ihub/common';
import { RouteObject } from 'react-router-dom';
import LazyCreateFlowWizard from '../components/create-flow-wizard/lazy-create-flow-wizard';
import LazyErrorPage from '../components/error-page/lazy-error-page';
import LazyConfigFlowWrapper from '../components/flows/flow-view/config-flow/lazy-config-flow-wrapper';
import LazyFlowList from '../components/flows/lazy-flow-list';
import LazyMainContainer from '../components/main-container/lazy-main-container';
import LazyMfaScreen from '../components/main-container/mfa-auth/lazy-mfa-screen';
import LazyMainDashboard from '../components/main-dashboard/lazy-main-dashboard';
import LazyClientSpaceListView from '../components/main-projects/client-spaces/client-space-list-view/lazy-client-space-list-view';
import LazyClientSpace from '../components/main-projects/client-spaces/lazy-client-space';
import LazyProjectSpace from '../components/main-projects/project-space/lazy-project-space';
import LazyMainSettings from '../components/main-settings/lazy-main-settings';
import LazyProfileDashboardView from '../components/profile-dashboard/lazy-profile-dashboard-view';
import LazyApiKeyEditView from '../components/security-management-view/api-key-management-view/api-key-edit-view/lazy-api-key-edit-view';
import LazyApiKeyManagementView from '../components/security-management-view/api-key-management-view/lazy-api-key-management-view';
import LazyRoleManagementView from '../components/security-management-view/role-management-view/lazy-role-management-view';
import LazyUserManagementView from '../components/security-management-view/user-management-view/lazy-user-management-view';
import LazyWhitelistIpManagementView from '../components/security-management-view/whitelist-ip-view/lazy-whitelist-ip-management-view';
import LazyWhitelistIpEditView from '../components/security-management-view/whitelist-ip-view/whitelist-ip-edit-view/lazy-whitelist-ip-edit-view';
import LazyTenantManagement from '../components/tenant-management/lazy-tenant-management';
import LazyTriggerView from '../components/triggers/lazy-trigger-view';
import LazyTriggerEditView from '../components/triggers/trigger-edit-view/lazy-trigger-edit-view';
import LazyUnauthorizedPage from '../components/unauthorized-page/lazy-unauthorized-page';
import AppRoutes from '../enums/app-routes';
import ProtectedRoute from './protected-route/protected-route';
import LazyIntegrationImportWizard from '../components/custom-controls/integration-import-wizard/lazy-integration-import-wizard';

const privateRoutes: RouteObject[] = [
	{
		path: AppRoutes.Root,
		element: (
			<ProtectedRoute>
				<LazyMainContainer />
			</ProtectedRoute>
		),
		errorElement: <LazyErrorPage />,
		children: [
			{
				path: AppRoutes.Dashboard,
				element: (
					<ProtectedRoute subject={Subjects.DashboardConfig} action={Actions.read}>
						<LazyMainDashboard />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ClientSpaceListRoute,
				element: (
					<ProtectedRoute subject={Subjects.ClientSpaceConfig} action={Actions.read}>
						<LazyClientSpaceListView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ClientSpaceRoute(),
				element: (
					<ProtectedRoute subject={Subjects.ClientSpaceConfig} action={Actions.read}>
						<LazyClientSpace />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ProjectSpace(),
				element: (
					<ProtectedRoute subject={Subjects.ProjectSpaceConfig} action={Actions.read}>
						<LazyProjectSpace />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.IntegrationSpace(),
				element: (
					<ProtectedRoute subject={Subjects.IntegrationConfig} action={Actions.read}>
						<LazyConfigFlowWrapper />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ImportIntegration(),
				element: (
					<ProtectedRoute subject={Subjects.IntegrationConfig} action={Actions.create}>
						<LazyIntegrationImportWizard />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.CreateFlowWizard,
				element: (
					<ProtectedRoute subject={Subjects.FlowConfig} action={Actions.read}>
						<LazyCreateFlowWizard />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.CreateFlowWizardWithContext(),
				element: (
					<ProtectedRoute subject={Subjects.FlowConfig} action={Actions.read}>
						<LazyCreateFlowWizard />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.Settings,
				element: (
					<ProtectedRoute subject={Subjects.ArchiveConfig} action={Actions.read}>
						<LazyMainSettings />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.FlowList,
				element: (
					<ProtectedRoute subject={Subjects.FlowConfig} action={Actions.read}>
						<LazyFlowList />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.Triggers,
				element: (
					<ProtectedRoute subject={Subjects.FlowConfig} action={Actions.read}>
						<LazyTriggerView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.TriggerEditView(),
				element: (
					<ProtectedRoute subject={Subjects.MicroTaskConfig} action={Actions.read}>
						<LazyTriggerEditView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.FlowView(),
				element: (
					<ProtectedRoute subject={Subjects.FlowConfig} action={Actions.read}>
						<LazyConfigFlowWrapper />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.TenantManagement(),
				element: (
					<ProtectedRoute subject={Subjects.TenantConfig} action={Actions.manage}>
						<LazyTenantManagement />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.UserManagement(),
				element: (
					<ProtectedRoute subject={Subjects.UserConfig} action={Actions.read}>
						<LazyUserManagementView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.RoleManagement(),
				element: (
					<ProtectedRoute subject={Subjects.RoleConfig} action={Actions.read}>
						<LazyRoleManagementView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ApiKeyManagement(),
				element: (
					<ProtectedRoute subject={Subjects.ApiKeyConfig} action={Actions.read}>
						<LazyApiKeyManagementView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ApiKeyEdit(),
				element: (
					<ProtectedRoute subject={Subjects.ApiKeyConfig} action={Actions.update}>
						<LazyApiKeyEditView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.IpWhitelistManagement(),
				element: (
					<ProtectedRoute subject={Subjects.WhitelistIpConfig} action={Actions.read}>
						<LazyWhitelistIpManagementView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.IpWhitelistEdit(),
				element: (
					<ProtectedRoute subject={Subjects.WhitelistIpConfig} action={Actions.update}>
						<LazyWhitelistIpEditView />
					</ProtectedRoute>
				),
			},
			{
				path: AppRoutes.ProfileDashboard(),
				element: (
					<ProtectedRoute subject={Subjects.UserProfile} action={Actions.read}>
						<LazyProfileDashboardView />
					</ProtectedRoute>
				),
			},
		],
	},
	{
		path: AppRoutes.Unauthorized(),
		element: (
			<ProtectedRoute>
				<LazyUnauthorizedPage />
			</ProtectedRoute>
		),
	},
	{
		path: AppRoutes.MfaScreen(),
		element: (
			<ProtectedRoute>
				<LazyMfaScreen />
			</ProtectedRoute>
		),
	},
];

export default privateRoutes;
